import ExcelJS from 'exceljs';

export class BTTExcelReport {
    /**
     * Generates and downloads an Excel report.
     * @param {Object} data - The data object.
     */
    static async exportToExcel(data: any) {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'AMV Monitor';
        workbook.created = new Date();
        const { worksite, machine } = data;
        let createFilename = new Date().toLocaleDateString() + '_' + machine.id + '_' + (worksite.projectName || 'Project') + '_' + (worksite.worksiteName || 'Worksite') + '_' + (worksite.tunnelName || 'Tunnel') + '_' + (worksite.peg || 'Peg') + '_' + (worksite.screen || 'Screen');
        data.selectedSections.forEach((report: string) => {
            switch (report) {
                case 'Frontpage': 
                    this.addGeneralInfoSheet(workbook, data);
                break;
                case 'Status':
                    if (data.statusDataList && data.statusDataList.length > 0) {
                        this.addStatusSheet(workbook, data.statusDataList);
                    }    
                break;
                // case 'BoltGraph': finalY = this.AddBoltGraphsSection(data, doc, finalY); break;
                case 'BoltPlacement':
                    if (data.boltGraphDataList && data.boltGraphDataList.length > 0) {
                        this.addBoltPlacementSheet(workbook, data.boltGraphDataList);
                    }    
                break;
                case 'HourCounter':
                    if (data.hoursDataList && data.hoursDataList.length > 0) {
                        this.addHourCountersSheet(workbook, data.hoursDataList);
                    }    
                break;
                // case 'Alarms':
                // break;

                default: break;
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        this._triggerDownload(buffer, createFilename + '.xlsx');
    }

    static addGeneralInfoSheet(workbook: ExcelJS.Workbook, data: any) {
        const sheet = workbook.addWorksheet('General Info');
        sheet.columns = [
            { header: 'Category', key: 'category', width: 25 },
            { header: 'Property', key: 'property', width: 25 },
            { header: 'Value', key: 'value', width: 40 }
        ];

        // Helper to add sections
        const addSection = (title: string, items: { label: string, value: any }[]) => {
            sheet.addRow([title]);
            sheet.lastRow!.font = { bold: true, size: 12 };
            sheet.lastRow!.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEEEEE' } };
            
            items.forEach(item => {
                sheet.addRow(['', item.label, item.value || '-']);
            });
            sheet.addRow([]); // Empty row for spacing
        };

        const { worksite, machine, timeInformation, peginformation } = data;

        if (worksite) {
            addSection('Project Information', [
                { label: 'Project Name', value: worksite.projectName },
                { label: 'Worksite', value: worksite.worksiteName },
                { label: 'Tunnel', value: worksite.tunnelName },
                { label: 'Peg', value: worksite.peg },
                { label: 'Screen', value: worksite.screen }
            ]);
        }

        if (machine) {
            addSection('Machine Information', [
                { label: 'Machine ID', value: machine.id },
                { label: 'Machine Type', value: machine.type }
            ]);
        }

        if (timeInformation) {
            addSection('Production Time', [
                { label: 'Start Time', value: timeInformation.startTime },
                { label: 'Stop Time', value: timeInformation.stopTime },
                { label: 'Total Duration', value: timeInformation.totalTime }
            ]);
        }

        if (peginformation) {
            addSection('Peg Information', [
                { label: 'Lowest registered pegnumber', value: peginformation.startPegNumber },
                { label: 'Highest registered pegnumber', value: peginformation.endPegNumber }
            ]);
        }
    }

    static addStatusSheet(workbook: ExcelJS.Workbook, statusData: any[]) {
        const sheet = workbook.addWorksheet('Bolt Status');
        sheet.columns = [
            { header: 'Peg', key: 'pegNumber', width: 10 },
            { header: 'Vault', key: 'vault', width: 10 },
            { header: 'Section', key: 'section', width: 10 },
            { header: 'Status', key: 'status', width: 20 },
            { header: 'Start', key: 'startTime', width: 20 },
            { header: 'Stop', key: 'stopTime', width: 20 },
            { header: 'Duration', key: 'totalTime', width: 15 },
            { header: 'Hold Time', key: 'holdTime', width: 15 },
            { header: 'Set Point', key: 'tonsSetPoint', width: 15 }
        ];

        statusData.forEach(s => {
            sheet.addRow({
                pegNumber: s.pegNumber,
                vault: s.vault,
                section: s.section,
                status: s.status,
                startTime: s.startTime ? new Date(s.startTime).toLocaleString() : '-',
                stopTime: s.stopTime ? new Date(s.stopTime).toLocaleString() : '-',
                totalTime: s.totalTime,
                holdTime: s.holdTime,
                tonsSetPoint: s.tonsSetPoint
            });
        });

        this._styleHeader(sheet);
    }

    static addBoltPlacementSheet(workbook: ExcelJS.Workbook, bolts: any[]) {
        const sheet = workbook.addWorksheet('Bolt Placement Data');
        sheet.columns = [
            { header: 'Peg', key: 'pegNumber', width: 10 },
            { header: 'Vault', key: 'vault', width: 10 },
            { header: 'Section', key: 'section', width: 10 },
            { header: 'Description', key: 'description', width: 30 },
            { header: 'Start Time', key: 'startTime', width: 20 },
            { header: 'Status ID', key: 'statusId', width: 10 }
        ];

        bolts.forEach(b => {
            sheet.addRow({
                pegNumber: b.pegNumber,
                vault: b.vault,
                section: b.section,
                description: b.description,
                startTime: b.startTime ? new Date(b.startTime).toLocaleString() : '-',
                statusId: b.statusId
            });
        });
        this._styleHeader(sheet);
    }

    static addHourCountersSheet(workbook: ExcelJS.Workbook, hourCounters: any[]) {
        const sheet = workbook.addWorksheet('Hour Counters');
        sheet.columns = [
            { header: 'Time', key: 'timestamp', width: 25 },
            { header: 'HPU Counter (h:min)', key: 'hpuMinCounter', width: 20 }
        ];
        
        if (hourCounters && hourCounters.length > 0) {
            const lastItem = hourCounters[hourCounters.length - 1];
            const totalMinutes = lastItem.hpuMinCounter || 0;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = Math.floor(totalMinutes % 60);
            const formattedCounter = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

            sheet.addRow({
                timestamp: lastItem.timestamp ? new Date(lastItem.timestamp).toLocaleString() : '-',
                hpuMinCounter: formattedCounter
            });
        }
        this._styleHeader(sheet);
    }

    static addAlarmsSheet(workbook: ExcelJS.Workbook, alarms: any[]) {
        const sheet = workbook.addWorksheet('Alarms');
        sheet.columns = [
            { header: 'Time', key: 'timestamp', width: 25 },
            { header: 'Code', key: 'alarmId', width: 15 },
            { header: 'Description', key: 'alarmNames', width: 50 }
        ];

        alarms.forEach(a => {
            sheet.addRow({
                timestamp: a.timestamp ? new Date(a.timestamp).toLocaleString() : '-',
                alarmId: a.alarmId,
                alarmNames: a.alarmNames
            });
        });
        this._styleHeader(sheet);
    }

    static _styleHeader(sheet: ExcelJS.Worksheet) {
        const row = sheet.getRow(1);
        row.font = { bold: true };
        row.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFCCCCCC' }
        };
    }

    static _triggerDownload(buffer: ArrayBuffer, filename: string) {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(link.href);
    }
}